import React, { useState, useEffect } from "react";
import apiService from "../services/api";
import {
  Modal,
  Select,
  NumberInput,
  Button,
  ActionIcon,
  Stack,
  Group,
  SimpleGrid,
  ScrollArea,
  Paper,
  Divider,
  Alert,
  Text,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconX, IconAlertCircle, IconAlertTriangle } from "@tabler/icons-react";
import { localizedField } from "../utils/localizedField";
import { formatPrice } from "../utils/formatPrice";
import dayjs from "dayjs";

export default function AddEditCustomerPriceModal({
  onClose,
  onSaved,
  editData = null,
}) {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);

  const [userId, setUserId] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  // 'validation' | 'server' — drives the Alert title so server errors don't masquerade as validation.
  const [formErrorKind, setFormErrorKind] = useState('validation');

  useEffect(() => {
    apiService.getUsers().then(setUsers).catch(console.error);
    apiService
      .getProducts({ lang: "en" })
      .then((raw) => {
        // Flatten parent products → individual variants for the dropdown
        const variants = [];
        for (const p of raw || []) {
          const name = localizedField(p, 'name', 'en');
          for (const v of p.variants || []) {
            if (v.is_hidden) continue;
            const weight =
              v.weight_value != null
                ? `${v.weight_value}${v.weight_unit || ""}`
                : "";
            variants.push({
              sku: v.sku,
              price: v.netPrice != null ? Number(v.netPrice) : (v.price != null ? Number(v.price) : null),
              vatRate: v.vatRate ?? p.vatRate ?? 0,
              label: weight
                ? `${name} – ${weight} (${v.sku})`
                : `${name} (${v.sku})`,
            });
          }
        }
        setProducts(variants);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (editData) {
      setUserId(editData.user_id);
      setItems([
        {
          id: Date.now(),
          product_sku: editData.variant_sku || editData.product_sku,
          price: editData.price,
          pricing_model: editData.pricing_model || "fixed",
          volume_type: editData.volume_type || "quantity",
          volume_threshold: editData.volume_threshold ?? "",
          post_threshold_price: editData.post_threshold_price ?? "",
          start_time: editData.start_time
            ? new Date(editData.start_time).toISOString().slice(0, 16)
            : "",
          end_time: editData.end_time
            ? new Date(editData.end_time).toISOString().slice(0, 16)
            : "",
        },
      ]);
    } else {
      handleAddRow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const handleAddRow = () => {
    setItems([
      ...items,
      {
        id: Date.now() + Math.random(),
        product_sku: "",
        price: "",
        pricing_model: "fixed",
        volume_type: "quantity",
        volume_threshold: "",
        post_threshold_price: "",
        start_time: "",
        end_time: "",
      },
    ]);
  };

  const handleRemoveRow = (id) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleItemChange = (id, field, value) => {
    setItems(
      items.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleProductChange = (id, sku) => {
    setItems(
      items.map((item) => {
        if (item.id !== id) return item;
        if (!sku || sku === item.product_sku) {
          return { ...item, product_sku: sku };
        }
        const variant = products.find((p) => p.sku === sku);
        const patch = { product_sku: sku };
        if (variant && variant.price != null) patch.price = variant.price;
        return { ...item, ...patch };
      })
    );
  };

  const getSelectedSkusWithExclusion = (currentId) => {
    return items
      .filter((it) => it.id !== currentId && it.product_sku)
      .map((it) => it.product_sku);
  };

  // Inline error: avoids the previous behavior where every validation
  // failure opened a NEW Mantine modal that stacked on top of older ones.
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!userId) {
      setFormErrorKind('validation');
      setFormError("Please select a user.");
      return;
    }
    if (items.length === 0) {
      setFormErrorKind('validation');
      setFormError("Please add at least one product price.");
      return;
    }
    if (items.some((it) => !it.product_sku || it.price === "" || it.price == null)) {
      setFormErrorKind('validation');
      setFormError("Please fill out all product and price fields.");
      return;
    }

    // Prevent negative prices (0 is allowed — e.g. free items)
    if (items.some((it) => Number(it.price) < 0)) {
      setFormErrorKind('validation');
      setFormError("Price cannot be negative.");
      return;
    }

    setFormError(null);
    setFormErrorKind('validation');
    setSaving(true);
    try {
      if (editData) {
        await apiService.updateCustomerPrice(editData.id, {
          user_id: userId,
          sku: items[0].product_sku,
          ...items[0],
        });
      } else {
        await apiService.createCustomerPrice({
          user_id: userId,
          items: items.map(
            ({
              product_sku,
              price,
              pricing_model,
              volume_type,
              volume_threshold,
              post_threshold_price,
              start_time,
              end_time,
            }) => ({
              sku: product_sku,
              price,
              pricing_model,
              volume_type,
              volume_threshold,
              post_threshold_price,
              start_time,
              end_time,
            })
          ),
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setFormErrorKind('server');
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Mantine Select expects { value, label } options
  const userOptions = users.map((u) => ({
    value: String(u.id),
    label: `${u.name} (${u.email})`,
  }));

  return (
    <Modal
      opened
      onClose={onClose}
      title={editData ? "Edit Custom Price" : "Add Custom Prices"}
      size="800px"
      centered
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="md">
          {formError && (
            <Alert
              id="customer-price-error"
              role="alert"
              icon={<IconAlertCircle size={16} aria-hidden="true" />}
              title={formErrorKind === 'server' ? 'Save failed' : 'Validation Error'}
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setFormError(null)}
            >
              {formError}
            </Alert>
          )}

          <Select
            label="Customer"
            placeholder="Select a user..."
            required
            data={userOptions}
            value={userId ? String(userId) : null}
            onChange={(value) => {
              setUserId(value);
              setFormError(null);
            }}
            disabled={!!editData}
            searchable
          />

          <ScrollArea.Autosize mah={600} type="auto" offsetScrollbars>
            <Stack gap="lg">
              {items.map((item) => {
                const disabledSkus = getSelectedSkusWithExclusion(item.id);
                const isVolume = item.pricing_model === "volume";

                const productOptions = products.map((p) => ({
                  value: p.sku,
                  label: p.label,
                  disabled: disabledSkus.includes(p.sku),
                }));

                const selectedVariant = products.find(
                  (p) => p.sku === item.product_sku
                );
                const storePrice =
                  selectedVariant && selectedVariant.price != null
                    ? Number(selectedVariant.price)
                    : null;
                const customPrice =
                  item.price === "" || item.price == null
                    ? null
                    : Number(item.price);
                const vatRate = Number(selectedVariant?.vatRate) || 0;
                const retailGrossPrice =
                  customPrice != null && vatRate > 0
                    ? customPrice * (1 + vatRate / 100)
                    : null;
                const priceAboveStore =
                  storePrice != null &&
                  customPrice != null &&
                  customPrice > storePrice;

                const priceHelperIds = [
                  retailGrossPrice != null ? `net-price-${item.id}` : null,
                  priceAboveStore ? `price-warning-${item.id}` : null,
                ]
                  .filter(Boolean)
                  .join(" ") || undefined;

                return (
                  <Paper key={item.id} withBorder={false} p={0}>
                    <Stack gap="sm">
                      <Group align="end" gap="sm" wrap="nowrap">
                        <Select
                          label="Product"
                          placeholder="Select a variant..."
                          required
                          searchable
                          data={productOptions}
                          value={item.product_sku || null}
                          onChange={(value) =>
                            handleProductChange(item.id, value)
                          }
                          style={{ flex: 2 }}
                        />

                        <Stack gap={4} style={{ flex: 1 }}>
                          <NumberInput
                            id={`price-input-${item.id}`}
                            label="Price excl. VAT (CHF)"
                            required
                            min={0}
                            decimalScale={2}
                            value={item.price}
                            onChange={(value) =>
                              handleItemChange(item.id, "price", value)
                            }
                            aria-describedby={priceHelperIds}
                            description={
                              priceHelperIds ? (
                                <>
                                  {retailGrossPrice != null && (
                                    <Text
                                      component="span"
                                      id={`net-price-${item.id}`}
                                      size="xs"
                                      c="dimmed"
                                    >
                                      Retail price (incl. VAT): {formatPrice(retailGrossPrice)}
                                    </Text>
                                  )}
                                  {priceAboveStore && (
                                    <Text
                                      component="span"
                                      id={`price-warning-${item.id}`}
                                      size="xs"
                                      c="orange"
                                    >
                                      <IconAlertTriangle
                                        size={12}
                                        style={{ verticalAlign: "text-bottom" }}
                                        aria-hidden="true"
                                      />
                                      {" "}Above store price {formatPrice(storePrice)}
                                    </Text>
                                  )}
                                </>
                              ) : undefined
                            }
                          />
                        </Stack>

                        <Select
                          label="Pricing Model"
                          data={[
                            { value: "fixed", label: "Fixed" },
                            { value: "volume", label: "Volume-based" },
                          ]}
                          value={item.pricing_model}
                          onChange={(value) =>
                            handleItemChange(item.id, "pricing_model", value)
                          }
                          allowDeselect={false}
                          style={{ flex: 1 }}
                        />

                        {!editData && (
                          <ActionIcon
                            color="red"
                            variant="filled"
                            onClick={() => handleRemoveRow(item.id)}
                            disabled={items.length === 1}
                            size="lg"
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        )}
                      </Group>

                      {isVolume && (
                        <Paper withBorder p="sm" radius="sm" bg="gray.0" component="fieldset">
                          <legend className="sr-only">Volume-based pricing configuration</legend>
                          <SimpleGrid cols={3} spacing="sm">
                            <Select
                              label="Volume Metric"
                              required={isVolume}
                              data={[
                                {
                                  value: "quantity",
                                  label: "Units (Quantity)",
                                },
                                { value: "weight", label: "Total Weight (Kg)" },
                              ]}
                              value={item.volume_type}
                              onChange={(value) =>
                                handleItemChange(item.id, "volume_type", value)
                              }
                              allowDeselect={false}
                            />
                            <NumberInput
                              label="Volume Threshold"
                              min={1}
                              step={1}
                              required={isVolume}
                              value={item.volume_threshold}
                              onChange={(value) =>
                                handleItemChange(
                                  item.id,
                                  "volume_threshold",
                                  value
                                )
                              }
                              placeholder={
                                item.volume_type === "weight"
                                  ? "e.g. 100 kg"
                                  : "e.g. 50 units"
                              }
                            />
                            <NumberInput
                              label="Discounted Price excl. VAT (CHF)"
                              min={0}
                              decimalScale={2}
                              required={isVolume}
                              value={item.post_threshold_price}
                              onChange={(value) =>
                                handleItemChange(
                                  item.id,
                                  "post_threshold_price",
                                  value
                                )
                              }
                              placeholder="Price after threshold"
                            />
                          </SimpleGrid>
                        </Paper>
                      )}

                      <SimpleGrid cols={2} spacing="sm">
                        <DateTimePicker
                          label="Start Time (Optional)"
                          placeholder="Pick date and time"
                          clearable
                          valueFormat="DD/MM/YYYY HH:mm"
                          value={
                            item.start_time
                              ? dayjs(item.start_time).toDate()
                              : null
                          }
                          onChange={(date) =>
                            handleItemChange(
                              item.id,
                              "start_time",
                              date ? dayjs(date).format("YYYY-MM-DDTHH:mm") : ""
                            )
                          }
                        />
                        <DateTimePicker
                          label="End Time (Optional)"
                          placeholder="Pick date and time"
                          clearable
                          valueFormat="DD/MM/YYYY HH:mm"
                          value={
                            item.end_time ? dayjs(item.end_time).toDate() : null
                          }
                          onChange={(date) =>
                            handleItemChange(
                              item.id,
                              "end_time",
                              date ? dayjs(date).format("YYYY-MM-DDTHH:mm") : ""
                            )
                          }
                        />
                      </SimpleGrid>
                    </Stack>
                    <Divider mt="lg" />
                  </Paper>
                );
              })}
            </Stack>
          </ScrollArea.Autosize>

          {!editData && (
            <Button
              variant="light"
              size="sm"
              onClick={handleAddRow}
              w="fit-content"
            >
              + Add Another Product
            </Button>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
